import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { stringify as toQueryString } from 'query-string';
import { IDictionary, SURVEY_TYPES, SurveyTypes } from '../../extViewmodels';
import { useApiClientContext } from '../../surveys/context';
import { ISurvey } from '../../surveys/models/Api';
import { reactQueryClient } from '../../web/ReactQueryProvider';

export const GET_SURVEY_BY_TOKEN_QUERY_KEY = 'getSurveyByTokenQuery';

export const useClientGetSurveyByToken = <T extends ISurvey>({
	accessToken,
	enabled = true,
	refetchOnWindowFocus = false,
	options = {},
	type,
}: {
	accessToken?: string;
	enabled?: boolean;
	refetchOnWindowFocus?: boolean;
	options?: IDictionary<string>;
	type?: SurveyTypes;
}) => {
	const { apiClient } = useApiClientContext();
	let urlBase = 'survey/byToken';

	if (type === SURVEY_TYPES.DonationSurvey) {
		urlBase = 'survey/donation/byToken';
	} else if (type === SURVEY_TYPES.EventRegistrationSurvey) {
		urlBase = 'survey/eventRegistration/byToken';
	} else if (type === SURVEY_TYPES.FileRequestSurvey) {
		urlBase = 'survey/fileRequest/byToken';
	}

	return useQuery({
		enabled,
		queryFn: () => {
			return apiClient.get<T>(`${urlBase}?${toQueryString({ token: accessToken, ...options })}`);
		},
		queryKey: [GET_SURVEY_BY_TOKEN_QUERY_KEY, type, accessToken, options],
		refetchOnWindowFocus,
		// Keep the previously-loaded survey while a new access token (e.g. the draft-token swap on
		// submit) refetches, so consumers that gate rendering on `data` don't unmount/remount the
		// whole response subtree — that unmount is a flicker (and drops local response state).
		placeholderData: keepPreviousData,
	});
};

export const invalidateSurveyByToken = () => {
	reactQueryClient.invalidateQueries({ queryKey: [GET_SURVEY_BY_TOKEN_QUERY_KEY] });
};
